/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  FilterTreeChecklistChangeBase,
  FilterTreeChildrenResultBase,
  FilterTreeNodeBase,
  FilterTreeSearchModeBase
} from './types';
import {
  collectFilterTreeBranchKeys,
  collectFilterTreeKeys,
  collectFilterTreeNodeKeys,
  filterTreeChecklistNodes,
  findCheckedFilterTreeNodes,
  findFilterTreeChecklistMatches,
  findFilterTreeNodePath,
  getFilterTreeNodeKey,
  getFilterTreeNodeSelectionState,
  isFilterTreeGroupFullyShown,
  updateFilterTreeNodeChildren,
  updateFilterTreeNodeLoading
} from './utils';

export const DEFAULT_FILTER_TREE_BATCH_SIZE = 10;

interface UseFilterTreeChecklistParams {
  batchSize: number;
  nodes: FilterTreeNodeBase[];
  onChange?: (change: FilterTreeChecklistChangeBase) => void;
  onLoadChildren?: (
    node: FilterTreeNodeBase
  ) => Promise<FilterTreeChildrenResultBase | FilterTreeNodeBase[] | void>;
  onSearchTermChange?: (term: string) => void;
  searchMode?: FilterTreeSearchModeBase;
  selectedIds: string[];
}

const normalizeLoadedChildren = (
  result: FilterTreeChildrenResultBase | FilterTreeNodeBase[]
): FilterTreeChildrenResultBase => (
  Array.isArray(result) ? {children: result, hasMore: false} : result
);

// resolves once the browser has painted what is on screen now ...
export const afterNextPaint = () => new Promise<void>(function(resolve) {
  const next = function() {
    setTimeout(resolve);
  };
  if (typeof globalThis.requestAnimationFrame === 'function') {
    globalThis.requestAnimationFrame(next);
  } else {
    next();
  }
});

/**
 * Tracks search, expansion, lazy loading, and selections for a tree checklist.
 *
 * ```typescript
 * import useFilterTreeChecklist from '@ion/dls/src/filters/useFilterTreeChecklist';
 *
 * ...
 *
 * const treeChecklist = useFilterTreeChecklist({
 *   batchSize,
 *   nodes,
 *   selectedIds,
 *   onChange,
 *   onLoadChildren
 * });
 * ```
 */
export const useFilterTreeChecklist = ({
  batchSize,
  nodes,
  onChange,
  onLoadChildren,
  onSearchTermChange,
  searchMode = 'filter',
  selectedIds
}: UseFilterTreeChecklistParams) => {
  const [treeNodes, setTreeNodes]         = useState<FilterTreeNodeBase[]>(nodes);
  const [expandedIds, setExpandedIds]     = useState<Set<string>>(new Set());
  const [searchActive, setSearchActive]   = useState(false);
  const [searchTerm, setSearchTerm]       = useState('');
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});
  const [pendingScrollKey, setPendingScrollKey] = useState<string | null>(null);
  // how many groups are loading their children right now ...
  const [loadingChildrenCount, setLoadingChildrenCount] = useState(0);
  // the group being expanded or shown in full, the loader showing under it ...
  const [busyNodeKey, setBusyNodeKey] = useState<string | null>(null);
  const selected                          = useMemo(() => new Set(selectedIds), [selectedIds]);
  const expandedIdsRef                    = useRef(expandedIds);
  const selectedRef                       = useRef(selected);
  const treeNodesRef                      = useRef(treeNodes);
  const visibleCountsRef                  = useRef(visibleCounts);
  expandedIdsRef.current                  = expandedIds;
  selectedRef.current                     = selected;
  treeNodesRef.current                    = treeNodes;
  visibleCountsRef.current                = visibleCounts;

  const resultsSearchMode = searchMode === 'results';
  const displayedNodes = useMemo(() => (
    searchActive && !resultsSearchMode ? filterTreeChecklistNodes(treeNodes, searchTerm) : treeNodes
  ), [resultsSearchMode, searchActive, searchTerm, treeNodes]);
  const searchExpandedIds = useMemo(() => new Set(
    searchActive && !resultsSearchMode && searchTerm.trim()
      ? collectFilterTreeBranchKeys(displayedNodes) : []
  ), [displayedNodes, resultsSearchMode, searchActive, searchTerm]);
  const effectiveExpandedIds = useMemo(() => new Set([
    ...Array.from(expandedIds),
    ...Array.from(searchExpandedIds)
  ]), [expandedIds, searchExpandedIds]);
  const searchResultCount = useMemo(() => (
    searchTerm.trim()
      ? findFilterTreeChecklistMatches(treeNodes, searchTerm).length
      : collectFilterTreeKeys(treeNodes).length
  ), [searchTerm, treeNodes]);
  const visibleRootNodes   = displayedNodes;
  const hasExpandedNodes   = !!expandedIds.size;
  const visibleSelectedIds = useMemo(() => new Set(
    collectFilterTreeKeys(displayedNodes)
  ), [displayedNodes]);
  const allVisibleSelected = !!visibleSelectedIds.size && Array.from(visibleSelectedIds)
    .every(key => selected.has(key));

  useEffect(() => {
    setTreeNodes(nodes);
  }, [nodes]);

  const emitChange = useCallback((
    nextSelected: Set<string>,
    sourceNodes = treeNodes
  ) => {
    onChange?.({
      selectedIds   : Array.from(nextSelected),
      selectedNodes : findCheckedFilterTreeNodes(sourceNodes, nextSelected)
    });
  }, [onChange, treeNodes]);

  const selectAll = useCallback(() => {
    const retainedSelected = searchActive ? Array.from(selectedRef.current)
      .filter(key => !visibleSelectedIds.has(key)) : [];
    emitChange(new Set([
      ...retainedSelected,
      ...Array.from(visibleSelectedIds)
    ]));
  }, [emitChange, searchActive, visibleSelectedIds]);

  const clear = useCallback(() => {
    emitChange(new Set());
  }, [emitChange]);

  const toggleNode = useCallback((node: FilterTreeNodeBase) => {
    const nodeKey      = getFilterTreeNodeKey(node);
    const keys         = collectFilterTreeNodeKeys(node);
    const nodePath     = findFilterTreeNodePath(treeNodesRef.current, nodeKey);
    const ancestors    = nodePath.slice(0, -1);
    const nextSelected = new Set(selectedRef.current);
    const shouldSelect = !nextSelected.has(nodeKey);
    keys.forEach(key => {
      if (shouldSelect) {
        nextSelected.add(key);
      } else {
        nextSelected.delete(key);
      }
    });
    if (shouldSelect) {
      ancestors.forEach(ancestor => {
        nextSelected.add(getFilterTreeNodeKey(ancestor));
      });
    }
    emitChange(nextSelected);
  }, [emitChange]);

  const loadNodeChildren = useCallback(async(node: FilterTreeNodeBase) => {
    const nodeKey = getFilterTreeNodeKey(node);
    const loadingTreeNodes = updateFilterTreeNodeLoading(treeNodesRef.current, nodeKey, true);
    treeNodesRef.current = loadingTreeNodes;
    setTreeNodes(loadingTreeNodes);
    setLoadingChildrenCount(count => count + 1);
    try {
      const result = await onLoadChildren?.(node);
      if (!result) {
        const nextTreeNodes = updateFilterTreeNodeLoading(treeNodesRef.current, nodeKey, false);
        treeNodesRef.current = nextTreeNodes;
        setTreeNodes(nextTreeNodes);
        return findFilterTreeNodePath(nextTreeNodes, nodeKey).pop();
      }
      const {children, hasMore = false} = normalizeLoadedChildren(result);
      const nextTreeNodes = updateFilterTreeNodeChildren(
        treeNodesRef.current,
        nodeKey,
        {children, hasMore}
      );
      treeNodesRef.current = nextTreeNodes;
      setTreeNodes(nextTreeNodes);
      if (getFilterTreeNodeSelectionState(node, selectedRef.current).checked) {
        const nextSelected = new Set(selectedRef.current);
        children.flatMap(collectFilterTreeNodeKeys).forEach(key => {
          nextSelected.add(key);
        });
        emitChange(nextSelected, nextTreeNodes);
      }
      return findFilterTreeNodePath(nextTreeNodes, nodeKey).pop();
    } catch (error) {
      const nextTreeNodes = updateFilterTreeNodeLoading(treeNodesRef.current, nodeKey, false);
      treeNodesRef.current = nextTreeNodes;
      setTreeNodes(nextTreeNodes);
      throw error;
    } finally {
      setLoadingChildrenCount(count => Math.max(count - 1, 0));
    }
  }, [emitChange, onLoadChildren]);

  const applyExpanded = useCallback((nextExpanded: Set<string>) => {
    expandedIdsRef.current = nextExpanded;
    setExpandedIds(nextExpanded);
  }, []);

  // expanding a group is one long render when it holds many subgroups or
  // shows every child after Show All - and its children may still have to
  // load - so the tree is busy, with its loader showing, from the click: the
  // group expands only once that has painted, and the tree is free again once
  // the expanded group has painted in turn ...
  const expandAfterPaint = useCallback(async(node: FilterTreeNodeBase, nextExpanded: Set<string>) => {
    const nodeKey = getFilterTreeNodeKey(node);
    setBusyNodeKey(nodeKey);
    setLoadingChildrenCount(count => count + 1);
    try {
      await afterNextPaint();
      applyExpanded(nextExpanded);
      if (node.hasMore && !node.childrenLoaded && onLoadChildren) {
        await loadNodeChildren(node);
      }
      await afterNextPaint();
    } finally {
      setBusyNodeKey(current => (current === nodeKey ? null : current));
      setLoadingChildrenCount(count => Math.max(count - 1, 0));
    }
  }, [applyExpanded, loadNodeChildren, onLoadChildren]);

  const toggleExpand = useCallback(async(node: FilterTreeNodeBase) => {
    const nodeKey = getFilterTreeNodeKey(node);
    const nextExpanded = new Set(expandedIdsRef.current);
    if (nextExpanded.has(nodeKey)) {
      nextExpanded.delete(nodeKey);
      applyExpanded(nextExpanded);
    } else {
      nextExpanded.add(nodeKey);
      await expandAfterPaint(node, nextExpanded);
    }
  }, [applyExpanded, expandAfterPaint]);

  // the tree is busy from the click, and its work starts only once that has
  // painted - showing every child of a loaded group is one long render, and
  // made the page look frozen before anything showed it was working ...
  const revealMore = useCallback(async(node: FilterTreeNodeBase) => {
    const nodeKey = getFilterTreeNodeKey(node);
    // 'show less' only once every child shows - a group opened part way (e.g.
    // by revealing a search result past its first batch) still shows all ...
    if (isFilterTreeGroupFullyShown(
      findFilterTreeNodePath(treeNodesRef.current, nodeKey).pop() ?? node,
      visibleCountsRef.current[nodeKey],
      batchSize
    )) {
      setVisibleCounts(current => ({
        ...current,
        [nodeKey] : batchSize
      }));
      return;
    }
    setBusyNodeKey(nodeKey);
    setLoadingChildrenCount(count => count + 1);
    try {
      await afterNextPaint();
      let currentNode = findFilterTreeNodePath(treeNodesRef.current, nodeKey).pop() ?? node;
      let previousChildCount = -1;
      while (currentNode.hasMore && previousChildCount !== currentNode.children?.length) {
        previousChildCount = currentNode.children?.length ?? 0;
        currentNode = await loadNodeChildren(currentNode) ?? currentNode;
      }
      setVisibleCounts(current => ({
        ...current,
        [nodeKey] : currentNode.children?.length ?? 0
      }));
    } finally {
      setBusyNodeKey(current => (current === nodeKey ? null : current));
      setLoadingChildrenCount(count => Math.max(count - 1, 0));
    }
  }, [batchSize, loadNodeChildren]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const activateSearch = useCallback(() => {
    setSearchActive(true);
  }, []);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchActive(true);
    setSearchTerm(event.target.value);
    onSearchTermChange?.(event.target.value);
  }, [onSearchTermChange]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    onSearchTermChange?.('');
  }, [onSearchTermChange]);

  const closeSearch = useCallback(() => {
    setSearchActive(false);
    setSearchTerm('');
    onSearchTermChange?.('');
  }, [onSearchTermChange]);

  // leaving an empty search box ends the search - a typed term stays
  // active (with its results) until it is cleared ...
  const blurSearch = useCallback(() => {
    if (!searchTerm.trim()) {
      setSearchActive(false);
    }
  }, [searchTerm]);

  const clearPendingScroll = useCallback(() => {
    setPendingScrollKey(null);
  }, []);

  /**
   * Pages a lazily-loaded parent's children (the same mechanism
   * `revealMore` uses) until the target key appears among them or its
   * pages are exhausted.
   */
  const loadUntilChildVisible = useCallback(async(
    parentNode: FilterTreeNodeBase,
    targetKey: string
  ) => {
    let currentNode = parentNode;
    let previousChildCount = -1;
    while (
      currentNode.hasMore &&
      !(currentNode.children ?? []).some(child => getFilterTreeNodeKey(child) === targetKey) &&
      previousChildCount !== (currentNode.children?.length ?? 0)
    ) {
      previousChildCount = currentNode.children?.length ?? 0;
      // eslint-disable-next-line no-await-in-loop
      currentNode = await loadNodeChildren(currentNode) ?? currentNode;
    }
    return currentNode;
  }, [loadNodeChildren]);

  /**
   * Expands every ancestor named in `ancestorKeys` in order, paging the
   * immediate parent's children (via `loadUntilChildVisible`) until
   * `targetKey` itself appears.
   */
  const expandAncestorPath = useCallback(async(ancestorKeys: string[], targetKey: string) => {
    const immediateParentKey = ancestorKeys[ancestorKeys.length - 1];
    for (const ancestorKey of ancestorKeys) {
      const ancestorPath = findFilterTreeNodePath(treeNodesRef.current, ancestorKey);
      let ancestorNode = ancestorPath[ancestorPath.length - 1];
      if (!ancestorNode) {
        continue;
      }
      if (!expandedIdsRef.current.has(ancestorKey)) {
        // eslint-disable-next-line no-await-in-loop
        await toggleExpand(ancestorNode);
        ancestorNode = findFilterTreeNodePath(treeNodesRef.current, ancestorKey).pop() ?? ancestorNode;
      }
      if (ancestorKey === immediateParentKey) {
        // eslint-disable-next-line no-await-in-loop
        await loadUntilChildVisible(ancestorNode, targetKey);
      }
    }
    // every step of the path stays visible - not just the target in its
    // parent: a subgroup past its own parent's first batch hid the whole
    // branch, the target included, so it was ticked but never shown ...
    const pathNodes = findFilterTreeNodePath(treeNodesRef.current, targetKey);
    setVisibleCounts(current => pathNodes.slice(0, -1).reduce(function(counts, parentNode, index) {
      const parentKey  = getFilterTreeNodeKey(parentNode);
      const childKey   = getFilterTreeNodeKey(pathNodes[index + 1]);
      const childIndex = (parentNode.children ?? []).findIndex(child => (
        getFilterTreeNodeKey(child) === childKey
      ));
      return childIndex < 0 ? counts : {
        ...counts,
        [parentKey] : Math.max(counts[parentKey] ?? batchSize, childIndex + 1)
      };
    }, current));
  }, [batchSize, loadUntilChildVisible, toggleExpand]);

  /**
   * Selects `targetKey` (and its ancestors) when it wasn't already
   * selected, matching the auto-check legacy applies to a search result
   * picked from its own tree search.
   */
  const selectRevealedNode = useCallback((targetKey: string) => {
    const targetPath = findFilterTreeNodePath(treeNodesRef.current, targetKey);
    const targetNode = targetPath[targetPath.length - 1];
    if (targetNode && !selectedRef.current.has(targetKey)) {
      const nextSelected = new Set(selectedRef.current);
      collectFilterTreeNodeKeys(targetNode).forEach(key => nextSelected.add(key));
      targetPath.slice(0, -1).forEach(ancestor => nextSelected.add(getFilterTreeNodeKey(ancestor)));
      // the tree as it is now - revealing may have just loaded the target, which
      // the tree this callback was created with doesn't hold yet, and the
      // applied filter is built from these nodes ...
      emitChange(nextSelected, treeNodesRef.current);
    }
  }, [emitChange]);

  /**
   * Expands every ancestor named in `nodePath`, auto-selects the target
   * when it wasn't already selected, then marks it as pending a
   * scroll-into-view - used to reveal a node found through an external
   * results list rather than in-place filtering.
   */
  const revealNodePath = useCallback(async(nodePath: string[]) => {
    const targetKey = nodePath[nodePath.length - 1];
    const ancestorKeys = nodePath.slice(0, -1);
    await expandAncestorPath(ancestorKeys, targetKey);
    selectRevealedNode(targetKey);
    setPendingScrollKey(targetKey ?? null);
  }, [expandAncestorPath, selectRevealedNode]);

  const isLoadingChildren = loadingChildrenCount > 0;

  return {
    activateSearch,
    busyNodeKey,
    allVisibleSelected,
    blurSearch,
    clear,
    clearPendingScroll,
    clearSearch,
    closeSearch,
    collapseAll,
    displayedNodes,
    effectiveExpandedIds,
    expandedIds,
    handleSearchChange,
    hasExpandedNodes,
    isLoadingChildren,
    pendingScrollKey,
    resultsSearchMode,
    revealMore,
    revealNodePath,
    searchActive,
    searchResultCount,
    searchTerm,
    selectAll,
    selected,
    toggleExpand,
    toggleNode,
    visibleCounts,
    visibleRootNodes
  };
};

export default useFilterTreeChecklist;
